import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Slider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CheckIcon, GaugeIcon, RotateCcwIcon } from 'lucide-react';
import { reasoningEffortFor, type ReasoningEffort } from '@inference.engine/model-registry/web';

export interface EffortControlProps {
  /** The model the next message goes to; nothing is drawn when its effort cannot be set. */
  model: string | undefined;
  value: unknown;
  onChange: (effort: PickableEffort) => void;
  disabled?: boolean;
}

type PickableEffort = Exclude<ReasoningEffort, 'none'>;

const pickable = (level: ReasoningEffort): level is PickableEffort => level !== 'none';

/** The levels a person picks between: `none` turns reasoning off, which is not a step towards less. */
export function effortLevelsFor(
  model: string | undefined,
): { levels: PickableEffort[]; recommended: PickableEffort } | undefined {
  const support = model ? reasoningEffortFor(model) : undefined;
  if (!support || !pickable(support.recommended)) return undefined;
  return { levels: support.levels.filter(pickable), recommended: support.recommended };
}

/**
 * How hard the model reasons on the next message, from Faster to Smarter over the levels the
 * model takes, with the recommended one marked. A pick applies when the popover closes: applied
 * while sliding, the button's label changed width under the open popover and moved it.
 */
export function EffortControl({ model, value, onChange, disabled = false }: EffortControlProps) {
  const { t } = useTranslation('chats');
  const [draft, setDraft] = useState<PickableEffort>();
  const labelId = useId();
  const support = effortLevelsFor(model);
  if (!support) return null;
  const { levels, recommended } = support;
  const current = levels.find((level) => level === value) ?? recommended;
  const shown = draft ?? current;
  const label = (level: PickableEffort) => t(`chats.chatInputV2.effort.${level}`);
  const position = (level: PickableEffort) =>
    `${(levels.indexOf(level) / (levels.length - 1)) * 100}%`;

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) return;
        if (draft && draft !== current) onChange(draft);
        setDraft(undefined);
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1 rounded-lg px-2 py-0.5 text-xs font-medium text-neutral-700"
              disabled={disabled}
              aria-label={t('chats.chatInputV2.effort.current', { level: label(current) })}
            >
              <GaugeIcon className="size-3.5" aria-hidden />
              {label(current)}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('chats.chatInputV2.effort.label')}</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="flex w-72 flex-col gap-sm">
        {/* Named by a group: Tessra's Slider puts an aria-label on its root, not on the thumb the
            person reaches, which would be left with no name. */}
        <div role="group" aria-labelledby={labelId} className="flex flex-col gap-sm">
          <div className="flex items-baseline justify-between">
            <span id={labelId} className="text-sm font-medium text-foreground">
              {t('chats.chatInputV2.effort.label')}
            </span>
            <span className="text-xs text-muted-foreground">{label(shown)}</span>
          </div>
          <div className="relative">
            <span
              aria-hidden
              className="absolute top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted-foreground"
              style={{ left: position(recommended) }}
            />
            <Slider
              min={0}
              max={levels.length - 1}
              step={1}
              value={[levels.indexOf(shown)]}
              onValueChange={([index]) => setDraft(levels[index])}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t('chats.chatInputV2.effort.faster')}</span>
            <span>{t('chats.chatInputV2.effort.smarter')}</span>
          </div>
        </div>
        {/* Always drawn, at one height: the popover opens upwards, so a row coming and going
            moved it under the pointer while sliding. */}
        <div className="-mx-1 flex h-6 items-center border-t border-border-lighter pt-xs">
          {shown === recommended ? (
            <span className="flex items-center gap-1 px-2 text-xs text-muted-foreground">
              <CheckIcon className="size-3" aria-hidden />
              {t('chats.chatInputV2.effort.recommended')}
            </span>
          ) : (
            <Button
              variant="ghost"
              size="mini"
              className="gap-1 text-muted-foreground"
              onClick={() => setDraft(recommended)}
            >
              <RotateCcwIcon className="size-3" aria-hidden />
              {t('chats.chatInputV2.effort.useRecommended', { level: label(recommended) })}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
